import {
  Component,
  Input,
  Output,
  ViewChild,
  EventEmitter,
  HostBinding
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { FileItem } from './file-item.interface';

const noop = () => {};

@Component({
  selector: 'suka-file-uploader',
  template: `
    <ng-container *ngIf="!skeleton; else skeletonTemplate">
      <label class="label">{{title}}</label>
      <div *ngIf="description" class="form__helper-text">{{description}}</div>
      <div class="file">
        <button
          type="button"
          [sukaButton]="buttonType"
          (click)="fileInput.click()"
          [attr.for]="fileUploaderId"
          [size]="size">
          {{buttonText}}
        </button>
        <input
          #fileInput
          type="file"
          class="file-input"
          [accept]="accept"
          [id]="fileUploaderId"
          [multiple]="multiple"
          tabindex="-1"
          (change)="onFilesAdded()"/>
        <div class="file-container">
          <div *ngFor="let fileItem of files">
            <suka-file [fileItem]="fileItem" (remove)="removeFile(fileItem)"></suka-file>
            <div *ngIf="fileItem.invalid" class="form-requirement">
              {{fileItem.invalidText}}
            </div>
          </div>
        </div>
      </div>
    </ng-container>
    <ng-template #skeletonTemplate>
      <div class="skeleton__text" style="width: 100px"></div>
      <div class="skeleton__text" style="width: 225px"></div>
      <button sukaButton skeleton="true"></button>
    </ng-template>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FileUploader,
      multi: true
    }
  ]
})
export class FileUploader {
  /**
   * Counter used to create unique ids for file-uploader components
   */
  static fileUploaderCount = 0;
  /**
   * Accessible text for the button that opens the upload window.
   */
  @Input() buttonText = 'Agregar archivo(s)';

  /**
   * Type set for button
   */
  @Input() buttonType: 'primary' | 'basic' | 'plain' | 'destructive' | 'outline_white' = 'basic';

  /**
   * Text set to the title
   */
  @Input() title: string;

  /**
   * Text set to the description
   */
  @Input() description: string;

  /**
   * Specify the types of files that the input should be able to receive
   */
  @Input() accept = [];

  /**
   * Set to `false` to tell the component to only accept a single file on upload.
   *
   * Defaults to `true`. Accepts multiple files.
   */
  @Input() multiple = true;

  /**
   * Set to `true` for a loading file uploader.
   */
  @Input() skeleton = false;

  /**
   * Sets the size of the button.
   */
  @Input() size: 'sm' | 'normal';

  /**
   * Provides a unique id for the underlying `<input>` node
   */
  @Input() fileUploaderId = `file-uploader-${FileUploader.fileUploaderCount}`;

  /**
   * Maintains a reference to the view DOM element of the underlying `<input>` node
   */
  @ViewChild('fileInput', {static: false}) fileInput;

  /**
   * The list of files that have been submitted to be uploaded
   */
  @Input() files = new Set<FileItem>();

  /**
   * Emits an event to other classes when files change
   */
  @Output() filesChange = new EventEmitter<any>();

  @HostBinding('class.file-uploader') baseClass = true;

  protected onTouchedCallback: () => void = noop;
  protected onChangeCallback: (_: Set<FileItem>) => void = noop;

  constructor() {
    FileUploader.fileUploaderCount++;
  }

  /**
   * Specifies the property to be used as the return value to `ngModel`
   */
  get value(): Set<FileItem> {
    return this.files;
  }
  set value(v: Set<FileItem>) {
    if (v !== this.files) {
      this.files = v;
      this.onChangeCallback(v);
    }
  }

  onBlur() {
    this.onTouchedCallback();
  }

  /**
   * Propagates the injected `value`.
   */
  writeValue(value: Set<FileItem>) {
    if (value !== this.value) {
      this.files = value;
    }
  }

  onFilesAdded() {
    const files = this.fileInput.nativeElement.files;
    if (!this.multiple) {
      this.files.clear();
    }
    for (let file of files) {
      const fileItem: FileItem = {
        file,
        uploaded: false,
        state: 'edit',
        invalid: false,
        invalidText: '',
      };
      this.files.add(fileItem);
      this.filesChange.emit(this.files);
    }

    this.value = this.files;
  }

  removeFile(fileItem) {
    this.files.delete(fileItem);
    this.fileInput.nativeElement.value = '';
    this.filesChange.emit(this.files);
  }

  /**
   * Registers the injected function to control the touch use of the `FileUploader`.
   */
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }
  /**
   * Sets a method in order to propagate changes back to the form.
   */
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }
}
